/**
 * Owner Settings — "ศูนย์ควบคุมระบบ" (System Control Panel).
 *
 * แบ่งเป็นหมวดแบบแท็บแทน scroll ยาวหน้าเดียว: บริษัท · รอบเงินเดือน · สูตรเวลา ·
 * เช็คอิน · AI · ความปลอดภัย. ค่าทั้งหมดเก็บใน settings key/value table
 * (ยกเว้นข้อมูลบริษัทที่มี endpoint แยก และ AI ที่เป็นหน้าแยก /owner/ai).
 */
import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Save, Download, Trash2, Building2, CalendarClock, Clock, MapPin, Sparkles, Shield, ChevronRight, UserPlus, Upload,
  ScrollText, Cloud, LayoutDashboard,
} from 'lucide-react';
import { OwnerAuditContent } from './Audit';
import { OwnerDriveConfigContent } from './DriveConfig';
import { AISettingsContent } from './AISettings';
import { ResetDataSection } from '../../components/ResetDataSection';
import { SettingsOverview } from '../../components/SettingsOverview';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import {
  useSettings, useUpdateSettings, useCompanyProfile, useUpdateCompanyProfile, type CompanyProfile,
  useOfficeLocationSuggestion, useApplyOfficeLocation,
} from '../../hooks';
import { useAuthStore } from '../../lib/store';
import { apiFetch, uploadFile } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

async function downloadCsv(path: string, filename: string, onError?: (msg: string) => void) {
  const jwt = useAuthStore.getState().jwt;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    onError?.(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
  }
}

type State = Record<string, string>;
// 'time' tab ย้ายไปอยู่ใน Time hub แล้ว (`/hr/time#settings`) — TabId ยังต้อง
// คงไว้เพื่อให้ FIELD_GROUPS filter ทำงาน (Time hub import FIELD_GROUPS + filter
// ด้วย tab='time'), แต่ TABS array ไม่โชว์ tab นี้แล้ว
type TabId = 'overview' | 'company' | 'payroll' | 'time' | 'checkin' | 'ai' | 'security' | 'audit' | 'drive' | 'reset';

// Order: setup flow → ใช้บางครั้ง → audit/backup (Owner-design 2026-05-28)
const TABS: Array<{ id: TabId; label: string; icon: typeof Building2 }> = [
  { id: 'overview', label: 'ภาพรวม', icon: LayoutDashboard },
  { id: 'company', label: 'บริษัท', icon: Building2 },
  { id: 'payroll', label: 'รอบเงินเดือน', icon: CalendarClock },
  { id: 'checkin', label: 'เช็คอิน', icon: MapPin },
  { id: 'security', label: 'ความปลอดภัย', icon: Shield },
  { id: 'ai', label: 'AI', icon: Sparkles },
  { id: 'audit', label: 'ประวัติการใช้งาน', icon: ScrollText },
  { id: 'drive', label: 'สำรองข้อมูล (Drive)', icon: Cloud },
  { id: 'reset', label: 'ล้างข้อมูล', icon: Trash2 },
];

interface FieldDef {
  key: string;
  label: string;
  type?: 'time' | 'number' | 'text' | 'select' | 'checkbox';
  hint?: string;
  options?: Array<{ value: string; label: string }>;
  step?: string;
  min?: string;
  max?: string;
}

/** กลุ่ม field ที่เก็บใน settings table — แต่ละกลุ่มผูกกับแท็บผ่าน `tab` */
const FIELD_GROUPS: Array<{ title: string; tab: TabId; fields: FieldDef[] }> = [
  {
    title: 'รอบเงินเดือนและการอนุมัติ',
    tab: 'payroll',
    fields: [
      { key: 'CUTOFF_DAY', label: 'วันปิดงวดเงินเดือน', type: 'number', min: '1', max: '31', hint: 'ใช้จริงกับการลา cron แจ้งเตือน AI rule และการปิดงวด (1-31)' },
      { key: 'PAYDAY_DAY', label: 'วันจ่ายเงินเดือน', type: 'number', min: '1', max: '31', hint: 'วันที่จ่ายประจำเดือน (1-31) — พิมพ์บน footer สลิป เว้นว่างได้' },
      {
        key: 'BACKDATED_MODE',
        label: 'นโยบายลาย้อนหลัง',
        type: 'select',
        options: [
          { value: 'lenient', label: 'ยืดหยุ่น — รับลาย้อนหลังแล้วส่งให้อนุมัติ' },
          { value: 'strict', label: 'เข้มงวด — ปฏิเสธลาย้อนหลังเกิน cutoff' },
        ],
      },
      { key: 'SOLO_MODE', label: 'โหมดเจ้าของคนเดียว', type: 'checkbox', hint: 'เปิด = Owner อนุมัติ payroll ได้เองโดยไม่ต้องรอ backup' },
      { key: 'SALARY_ONLY', label: 'โหมดเงินเดือนล้วน', type: 'checkbox', hint: 'เปิด = ไม่ใช้ระบบลงเวลา คิดเงินจากฐานเงินเดือน เงินเพิ่ม รายการหัก และภาษี/ประกันสังคม (จ่ายเต็ม ไม่หักสาย/ขาด ไม่จ่าย OT)' },
      { key: 'SKIP_AUTO_STATUTORY', label: 'ไม่คิดภาษี/ประกันสังคมอัตโนมัติ (กรอกเองในไฟล์)', type: 'checkbox', hint: 'เปิด = ระบบไม่คำนวณ ภาษี/ประกันสังคม/PF ให้ ใช้ตัวเลขจากไฟล์นำเข้าล้วน · สุทธิ = เงินเดือน + เงินเพิ่มที่กรอก − เงินหักที่กรอก · ใส่ ปกส./ภาษี เป็นคอลัมน์ "หัก:" ในไฟล์เอง' },
      { key: 'HOLIDAY_WORK_NO_PREMIUM', label: 'ทำงานวันหยุดประเพณี = คิดเหมือนวันทำงานปกติ', type: 'checkbox', hint: 'เปิด = ใครมาทำงานวันหยุดตามประเพณีจะได้ค่าจ้าง 1 เท่าเท่าวันทำงานปกติ ระบบไม่บวกค่าทำงานวันหยุด (OT2) ให้ · ปิด (ค่าเริ่มต้น) = บวกค่าทำงานวันหยุดอัตโนมัติ ⚠️ กฎหมายแรงงาน ม.62 กำหนดให้จ่ายเพิ่มเมื่อทำงานวันหยุด เปิดโหมดนี้ = จ่ายต่ำกว่าที่กฎหมายกำหนด ใช้เมื่อบริษัทตกลงกับพนักงานแล้วเท่านั้น' },
      { key: 'BACKUP_APPROVER_LAG_DAYS', label: 'HR backup approve หลังสิ้นงวด (วัน)', type: 'number', min: '0', max: '31', hint: 'ใช้เมื่อ Owner ไม่อนุมัติทันเวลา (0–31 วัน)' },
      {
        key: 'COMMISSION_LAG_MONTHS',
        label: 'จ่ายคอมมิชชั่นล่าช้า',
        type: 'select',
        options: [
          { value: '0', label: 'เดือนเดียวกัน' },
          { value: '1', label: 'เลื่อน 1 เดือน' },
        ],
      },
      { key: 'VARIANCE_THRESHOLD', label: 'เกณฑ์เตือนเงินเดือนเปลี่ยน (%)', type: 'number', hint: 'ใช้กับตรวจก่อนปิดงวดและ smart warning' },
    ],
  },
  {
    title: 'วันทำงานและสูตรเวลา',
    tab: 'time',
    fields: [
      { key: 'WORKDAYS_PER_WEEK', label: 'จำนวนวันทำงานต่อสัปดาห์', type: 'number', min: '1', max: '7', hint: '1-7' },
      { key: 'WEEKEND_DAYS', label: 'วันหยุดประจำสัปดาห์', type: 'text', hint: 'เลขวันแบบ ISO คั่นด้วย comma: 1=จันทร์ … 7=อาทิตย์ เช่น 6,7 · เว้นว่าง = ทำงานทุกวัน ไม่มีวันหยุดประจำสัปดาห์' },
      { key: 'HOURS_PER_WORKDAY', label: 'ชั่วโมงทำงานต่อวัน', type: 'number', step: '0.25', min: '1', max: '24', hint: 'ใช้คิดรายวัน/รายชั่วโมง/OT' },
      { key: 'LATE_GRACE_MINUTES', label: 'สายได้ไม่เกิน (นาที)', type: 'number', min: '0', max: '240', hint: 'ใช้กับ shift-aware attendance' },
      { key: 'OT_WEEKLY_CAP_HOURS', label: 'เพดาน OT ต่อสัปดาห์ (ชั่วโมง)', type: 'number', step: '0.25', min: '0', max: '168', hint: 'ค่าแรงไทยทั่วไปใช้ 36 ชม./สัปดาห์' },
    ],
  },
  {
    title: 'จำนวนรอบและเวลาเช็คอิน',
    tab: 'checkin',
    fields: [
      { key: 'REQUIRE_LUNCH_SCAN', label: 'ให้สแกนพักเที่ยงด้วย (4 รอบ)', type: 'checkbox', hint: 'ปิด = เช็คอิน 2 รอบ (เข้างาน/เลิกงาน) · เปิด = 4 รอบ (เพิ่มออกพัก/กลับจากพัก)' },
      { key: 'WORK_MORNING_IN_TIME', label: 'เข้างาน (เช้า)', type: 'time' },
      { key: 'WORK_LUNCH_OUT_TIME', label: 'พักเที่ยง (ออกพัก)', type: 'time', hint: 'ใช้เมื่อเปิดสแกนพักเที่ยง' },
      { key: 'WORK_LUNCH_IN_TIME', label: 'กลับจากพัก', type: 'time', hint: 'ใช้เมื่อเปิดสแกนพักเที่ยง' },
      { key: 'WORK_EVENING_OUT_TIME', label: 'เลิกงาน', type: 'time' },
    ],
  },
  {
    title: 'การเตือน (cron)',
    tab: 'checkin',
    fields: [
      { key: 'CHECKIN_GRACE_MINUTES', label: 'รอ (นาที) ก่อนเตือนครั้งแรก', type: 'number', hint: 'เช่น 15 = เลยเวลา 15 นาทียังไม่เช็คอินจึงเตือน' },
      { key: 'REMINDER_INTERVAL_MINUTES', label: 'เว้นช่วงระหว่างเตือน (นาที)', type: 'number', hint: 'เริ่มต้น 5' },
      { key: 'REMINDER_COUNT_MAX', label: 'จำนวนครั้งเตือนสูงสุด', type: 'number', hint: 'เริ่มต้น 3' },
    ],
  },
  {
    title: 'พื้นที่ออฟฟิศ (geofence)',
    tab: 'checkin',
    fields: [
      { key: 'GEOFENCE_ENABLED', label: 'ตรวจพิกัดตอนเช็คอิน', type: 'checkbox', hint: 'ปิด = เช็คอินจากที่ไหนก็ผ่าน (เหมาะกับงานนอกสถานที่/หลายสาขา)' },
      { key: 'OFFICE_LATITUDE', label: 'Latitude', type: 'text', hint: 'เช่น 13.7563' },
      { key: 'OFFICE_LONGITUDE', label: 'Longitude', type: 'text', hint: 'เช่น 100.5018' },
      { key: 'OFFICE_RADIUS_METERS', label: 'รัศมีที่อนุญาต (เมตร)', type: 'number', min: '20', max: '5000', hint: 'เริ่มต้น 200 — ต่ำกว่า 20 ม. เสี่ยงสแกนไม่ผ่านเพราะ GPS คลาดเคลื่อน' },
    ],
  },
];

const COMPANY_FIELDS: Array<{ key: keyof CompanyProfile; label: string; hint?: string }> = [
  { key: 'company_name', label: 'ชื่อบริษัท (ตามหนังสือรับรอง)', hint: 'เช่น บริษัท มูดาต้า จำกัด' },
  { key: 'company_name_en', label: 'ชื่อบริษัท (ภาษาอังกฤษ)', hint: 'ใช้บนหัวหนังสือรับรองเวอร์ชันอังกฤษ เช่น Mudata Co., Ltd. — เว้นว่าง = ใช้ชื่อไทย' },
  { key: 'tax_id', label: 'เลขประจำตัวผู้เสียภาษี / นิติบุคคล (13 หลัก)', hint: 'แสดงบนสลิป + 50 ทวิ' },
  { key: 'company_phone', label: 'โทรศัพท์', hint: 'เช่น 02-123-4567' },
  { key: 'company_address', label: 'ที่อยู่บริษัท', hint: 'ที่อยู่เต็มสำหรับเอกสาร' },
  { key: 'company_address_en', label: 'ที่อยู่บริษัท (ภาษาอังกฤษ)', hint: 'ใช้บนหนังสือรับรอง EN — เว้นว่าง = ใช้ที่อยู่ไทย' },
  { key: 'signer_name', label: 'ชื่อผู้มีอำนาจลงนาม', hint: 'เว้นว่าง = สลิประบุ "ไม่ต้องลงนาม"; ใส่ชื่อ = พิมพ์ช่องลงนามบนสลิป' },
  { key: 'signer_name_en', label: 'ชื่อผู้ลงนาม (ภาษาอังกฤษ)', hint: 'ใช้บนหนังสือรับรอง EN เช่น Ms. Janjira Kamon — เว้นว่าง = ใช้ชื่อไทย' },
  { key: 'signer_title', label: 'ตำแหน่งผู้ลงนาม', hint: 'เช่น กรรมการผู้จัดการ / ผู้จัดการฝ่ายบุคคล' },
  { key: 'signer_title_en', label: 'ตำแหน่งผู้ลงนาม (ภาษาอังกฤษ)', hint: 'เช่น Managing Director — เว้นว่าง = ใช้ตำแหน่งไทย' },
];

export { FIELD_GROUPS };
export type { FieldDef, TabId };
export function Field({ f, value, onChange }: { f: FieldDef; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label">{f.label}</label>
      {f.type === 'select' ? (
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          {(f.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : f.type === 'checkbox' ? (
        <button
          type="button"
          onClick={() => onChange(value === 'true' ? 'false' : 'true')}
          className={`relative h-7 w-12 rounded-full transition ${value === 'true' ? 'bg-brand-600' : 'bg-gray-300'}`}
          aria-pressed={value === 'true'}
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition ${value === 'true' ? 'left-6' : 'left-1'}`} />
        </button>
      ) : (
        <input
          type={f.type ?? 'text'}
          step={f.step}
          min={f.min}
          max={f.max}
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.hint}
        />
      )}
      {f.hint && <div className="text-xs text-gray-500 mt-1">{f.hint}</div>}
    </div>
  );
}

export function OwnerSettings() {
  const { data, isLoading } = useSettings();
  const update = useUpdateSettings();
  const toast = useToast();
  const { data: company } = useCompanyProfile();
  const updateCompany = useUpdateCompanyProfile();
  // Pre-fill tab from URL hash so legacy /owner/audit and /owner/drive
  // redirects (Navigate → /owner/settings#audit / #drive) land on the
  // right tab. Only honor IDs that actually exist in TABS — 'time' is
  // gone from the UI (lives in Time hub) so a stray #time stays on
  // 'company' instead of rendering an unreachable section.
  const [tab, setTab] = useState<TabId>(() => {
    if (typeof window === 'undefined') return 'company';
    const h = window.location.hash.replace('#', '');
    return TABS.some((t) => t.id === h) ? (h as TabId) : 'overview';
  });
  // Mirror tab into hash so reload / share-link works.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${tab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  }, [tab]);
  // sync tab จาก #hash เมื่อ navigate ผ่าน sidebar (เมนูย่อย)
  const __loc = useLocation();
  useEffect(() => {
    const h = __loc.hash.replace('#', '');
    if (TABS.some((t) => t.id === h)) setTab(h as TabId);
  }, [__loc.hash]);

  const [form, setForm] = useState<State>({});
  const [companyForm, setCompanyForm] = useState<Partial<CompanyProfile>>({});
  const [companySaved, setCompanySaved] = useState(false);
  const [saved, setSaved] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);
  useEffect(() => {
    if (company) setCompanyForm(company);
  }, [company]);

  async function handleSaveCompany() {
    try {
      await updateCompany.mutateAsync(companyForm);
      setCompanySaved(true);
      toast.success('บันทึกข้อมูลบริษัทแล้ว');
      setTimeout(() => setCompanySaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (isLoading || !data) {
    return (
      <AdminLayout title="ตั้งค่าระบบ">
        <Loading />
      </AdminLayout>
    );
  }

  function setField(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      toast.error('อุปกรณ์นี้ไม่รองรับการอ่านตำแหน่ง');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('OFFICE_LATITUDE', pos.coords.latitude.toFixed(6));
        setField('OFFICE_LONGITUDE', pos.coords.longitude.toFixed(6));
        setGeoLoading(false);
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED ? 'ไม่ได้รับอนุญาตให้เข้าถึงตำแหน่ง — เปิดสิทธิ์ตำแหน่งในเบราว์เซอร์แล้วลองใหม่' :
          err.code === err.POSITION_UNAVAILABLE ? 'หาตำแหน่งไม่ได้ ลองใหม่อีกครั้ง' :
          err.code === err.TIMEOUT ? 'อ่านตำแหน่งนานเกินไป ลองใหม่อีกครั้ง' :
          'อ่านตำแหน่งไม่สำเร็จ';
        toast.error(msg);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  async function handleSave() {
    // เช็คค่าตัวเลขที่มีช่วงจำกัดก่อนส่ง — ค่าผิดแม้ช่องเดียวทำให้ทั้งฟอร์มเซฟไม่ได้
    // (เดิม error ถูกกลืนเงียบ ผู้ใช้เลยเห็นเหมือน "กดเซฟไม่ได้")
    for (const g of FIELD_GROUPS) {
      for (const f of g.fields) {
        if (f.type !== 'number') continue;
        const raw = form[f.key];
        if (raw === undefined || raw === '') continue;
        const n = Number(raw);
        const lo = f.min !== undefined ? Number(f.min) : undefined;
        const hi = f.max !== undefined ? Number(f.max) : undefined;
        if (!Number.isFinite(n) || (lo !== undefined && n < lo) || (hi !== undefined && n > hi)) {
          const range = lo !== undefined && hi !== undefined ? ` (ต้องอยู่ระหว่าง ${lo}–${hi})` : '';
          toast.error(`ค่าในช่อง "${f.label}" ไม่ถูกต้อง${range}`);
          return;
        }
      }
    }
    try {
      await update.mutateAsync(form);
      setSaved(true);
      toast.success('บันทึกการตั้งค่าแล้ว');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  const groupsForTab = FIELD_GROUPS.filter((g) => g.tab === tab);

  return (
    <AdminLayout title="ตั้งค่าระบบ">
      {/* แท็บหมวดหมู่ — ศูนย์ควบคุมระบบ */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition ${
                active ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <SettingsOverview />}

      {/* บริษัท */}
      {tab === 'company' && (
        <div className="card p-4 border-brand-200">
          <div className="font-semibold mb-1">ข้อมูลบริษัท (สำหรับเอกสารสำคัญ)</div>
          <p className="text-xs text-gray-500 mb-3">ใช้พิมพ์บนหัวสลิปเงินเดือน หนังสือรับรองหักภาษี (50 ทวิ) และเอกสารราชการ</p>

          {/* โลโก้บริษัท — แสดงบนหัวสลิป */}
          <div className="mb-4">
            <label className="label">โลโก้บริษัท (PNG/JPG ≤ 5 MB)</label>
            <div className="mt-1 flex items-center gap-3">
              {companyForm.logo_url ? (
                <img src={companyForm.logo_url} alt="logo" className="h-14 w-14 object-contain rounded border border-gray-200 bg-gray-50" />
              ) : (
                <div className="h-14 w-14 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">ไม่มี</div>
              )}
              <div className="flex gap-2">
                <label className="btn-secondary cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const path = await uploadFile(file, 'logo', file.name);
                        await updateCompany.mutateAsync({ logo_path: path });
                        toast.success('อัปโหลดโลโก้สำเร็จ');
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'อัปโหลดไม่สำเร็จ');
                      } finally {
                        e.target.value = ''; // allow re-selecting same file
                      }
                    }}
                  />
                  อัปโหลด
                </label>
                {companyForm.logo_path && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        await updateCompany.mutateAsync({ logo_path: '' });
                        toast.success('ลบโลโก้แล้ว');
                      } catch (err) {
                        toast.error(err instanceof Error ? err.message : 'ลบไม่สำเร็จ');
                      }
                    }}
                  >
                    ลบ
                  </button>
                )}
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">แสดงมุมซ้ายบนของสลิปทุกใบ · สูงสุด 48px ในเอกสาร</div>
          </div>

          <div className="space-y-3">
            {COMPANY_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="label">{f.label}</label>
                {f.key === 'company_address' ? (
                  <textarea rows={2} className="input resize-none" value={companyForm[f.key] ?? ''} onChange={(e) => { setCompanyForm((p) => ({ ...p, [f.key]: e.target.value })); setCompanySaved(false); }} placeholder={f.hint} />
                ) : (
                  <input className="input" value={companyForm[f.key] ?? ''} onChange={(e) => { setCompanyForm((p) => ({ ...p, [f.key]: e.target.value })); setCompanySaved(false); }} placeholder={f.hint} />
                )}
                {f.hint && <div className="text-xs text-gray-500 mt-1">{f.hint}</div>}
              </div>
            ))}
          </div>
          <button onClick={handleSaveCompany} disabled={updateCompany.isPending} className="btn-primary w-full mt-3">
            <Save className="w-5 h-5" /> {updateCompany.isPending ? 'กำลังบันทึก…' : companySaved ? 'บันทึกแล้ว ✓' : 'บันทึกข้อมูลบริษัท'}
          </button>
        </div>
      )}

      {/* ผูกบริษัทเพิ่ม — Owner ที่ดูแลหลายบริษัท */}
      {tab === 'company' && (
        <div className="card p-4 border-brand-200">
          <div className="font-semibold mb-1">ดูแลหลายบริษัท?</div>
          <p className="text-xs text-gray-500 mb-3">
            บัญชี LINE เดียวผูกได้หลายบริษัท — สลับไปมาได้จาก dropdown มุมซ้ายบน
          </p>
          <div className="grid grid-cols-1 gap-2">
            <Link to="/pair" className="btn-secondary text-sm justify-center">
              <Building2 className="w-4 h-4" /> ผูกกับบริษัทที่มีอยู่แล้ว (ใส่รหัสจาก Owner)
            </Link>
            <Link to="/signup" className="btn-secondary text-sm justify-center">
              <UserPlus className="w-4 h-4" /> สมัครบริษัทใหม่
            </Link>
          </div>
        </div>
      )}

      {/* รอบเงินเดือน / สูตรเวลา / เช็คอิน — ค่าใน settings table */}
      {groupsForTab.length > 0 && (
        <>
          {groupsForTab.map((group) => (
            <Fragment key={group.title}>
              <div className="card p-4 mb-3">
                <div className="font-semibold mb-3">{group.title}</div>
                {group.title === 'พื้นที่ออฟฟิศ (geofence)' && (
                  <GeofenceModeSelect
                    value={(form.GEOFENCE_MODE as 'off' | 'observe' | 'enforce' | undefined) ?? 'observe'}
                    onChange={(v) => setField('GEOFENCE_MODE', v)}
                  />
                )}
                <div className="space-y-3">
                  {group.fields.map((f) => (
                    <Field key={f.key} f={f} value={form[f.key] ?? ''} onChange={(v) => setField(f.key, v)} />
                  ))}
                </div>
                {group.title === 'พื้นที่ออฟฟิศ (geofence)' && (
                  <>
                    <button
                      type="button"
                      onClick={handleUseCurrentLocation}
                      disabled={geoLoading}
                      className="btn-secondary text-sm mt-3 w-full justify-center"
                    >
                      <MapPin className="w-4 h-4" /> {geoLoading ? 'กำลังอ่านตำแหน่ง…' : '📍 ใช้ตำแหน่งปัจจุบัน'}
                    </button>
                    {form.GEOFENCE_ENABLED === 'true' && form.OFFICE_LATITUDE === '13.7563' && form.OFFICE_LONGITUDE === '100.5018' && (
                      <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mt-3">
                        ⚠️ พิกัดนี้เป็นค่าตัวอย่าง ไม่ใช่ที่ตั้งออฟฟิศจริง — พนักงานที่สแกนเข้างานจะถูกตีว่าอยู่นอกพื้นที่และต้องรอ HR ตรวจสอบทุกครั้ง กดปุ่มใช้ตำแหน่งปัจจุบันขณะอยู่ที่ออฟฟิศเพื่อแก้
                      </div>
                    )}
                  </>
                )}
              </div>
              {group.title === 'พื้นที่ออฟฟิศ (geofence)' && (
                <OfficeLocationSuggestionCard
                  onApplied={(lat, lng, radiusM) => {
                    setField('OFFICE_LATITUDE', lat.toFixed(6));
                    setField('OFFICE_LONGITUDE', lng.toFixed(6));
                    setField('OFFICE_RADIUS_METERS', String(radiusM));
                    setField('GEOFENCE_MODE', 'enforce');
                  }}
                />
              )}
            </Fragment>
          ))}
          <button onClick={handleSave} disabled={update.isPending} className="btn-primary w-full">
            <Save className="w-5 h-5" />
            {update.isPending ? 'กำลังบันทึก…' : saved ? 'บันทึกแล้ว ✓' : 'บันทึก'}
          </button>
        </>
      )}

      {/* AI — absorbed from /owner/ai */}
      {tab === 'ai' && <AISettingsContent />}

      {/* ความปลอดภัย — export + danger zone */}
      {tab === 'security' && (
        <>
          <WebLoginZone />
          <div className="card p-4 mb-3">
            <div className="font-semibold mb-2">Export ข้อมูล (CSV)</div>
            <p className="text-xs text-gray-500 mb-3">สำรองข้อมูลออกมาเก็บเอง — UTF-8 BOM เปิดใน Excel ภาษาไทยได้</p>
            <div className="grid grid-cols-1 gap-2">
              <button onClick={() => downloadCsv('/owner/export/employees', 'employees.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> พนักงานทั้งหมด
              </button>
              <button onClick={() => downloadCsv(`/owner/export/leaves?year=${new Date().getFullYear()}`, 'leaves-ot.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> ใบลา + OT ปีนี้
              </button>
              <button onClick={() => downloadCsv(`/owner/export/payroll?year=${new Date().getFullYear()}`, 'payroll.csv', toast.error)} className="btn-secondary text-sm">
                <Download className="w-4 h-4" /> Payroll ปีนี้
              </button>
            </div>
          </div>
          <EmployeePortableZone />
          <DeleteTenantZone />
        </>
      )}

      {/* ประวัติการใช้งานระบบ — absorbed from /owner/audit */}
      {tab === 'audit' && <OwnerAuditContent />}

      {/* สำรองข้อมูลขึ้น Drive — absorbed from /owner/drive */}
      {tab === 'drive' && <OwnerDriveConfigContent />}
      {tab === 'reset' && <ResetDataSection />}
    </AdminLayout>
  );
}

type GeofenceMode = 'off' | 'observe' | 'enforce';
const GEOFENCE_MODE_OPTIONS: Array<{ value: GeofenceMode; label: string; desc: string }> = [
  { value: 'off', label: 'ปิด', desc: 'ปิด — ไม่ตรวจพิกัดเลย เช็คอินจากที่ไหนก็ได้' },
  { value: 'observe', label: 'เรียนรู้', desc: 'เรียนรู้ — บันทึกพิกัดไว้เรียนรู้ที่ตั้งออฟฟิศ แต่ยังไม่บล็อกใคร' },
  { value: 'enforce', label: 'บังคับ', desc: 'บังคับ — พนักงานที่อยู่นอกพื้นที่ต้องรอ HR ตรวจสอบ' },
];

/** เลือกโหมดตรวจพิกัด + คำอธิบายใต้แต่ละตัวเลือก + badge เตือนถาวรตอนยังเป็นโหมดเรียนรู้ */
export function GeofenceModeSelect({ value, onChange }: { value: GeofenceMode; onChange: (v: GeofenceMode) => void }) {
  return (
    <div className="mb-4">
      <label className="label">โหมดตรวจพิกัด</label>
      <select
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value as GeofenceMode)}
      >
        {GEOFENCE_MODE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <div className="mt-2 space-y-1">
        {GEOFENCE_MODE_OPTIONS.map((o) => (
          <div
            key={o.value}
            className={`text-xs rounded px-2 py-1 ${value === o.value ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-400'}`}
          >
            {o.desc}
          </div>
        ))}
      </div>
      {value === 'observe' && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">
          ⚠️ ยังไม่ได้บังคับตรวจพิกัด — พนักงานเช็คอินจากไหนก็ผ่านหมด
        </div>
      )}
    </div>
  );
}

/**
 * การ์ดพิกัดที่ระบบเรียนรู้ได้จากการเช็คอินจริง — ดึงจาก
 * GET /owner/office-location/suggestion. ยืนยันได้เฉพาะตอน confident=true
 * เท่านั้น (ยังไม่มั่นใจ = ไม่มีปุ่มยืนยัน กันตั้งพิกัดผิดจากข้อมูลกระจัดกระจาย)
 */
export function OfficeLocationSuggestionCard({ onApplied }: { onApplied: (lat: number, lng: number, radiusM: number) => void }) {
  const { data, isLoading } = useOfficeLocationSuggestion();
  const apply = useApplyOfficeLocation();
  const consequence = useConsequence();
  const toast = useToast();

  if (isLoading || !data) {
    return (
      <div className="card p-4 mb-3">
        <div className="font-semibold mb-1">พิกัดที่ระบบเรียนรู้ได้จากการเช็คอินจริง</div>
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      </div>
    );
  }

  const s = data.suggestion;
  const confident = !!s && s.confident && s.lat != null && s.lng != null && s.radius_m != null;

  async function handleApply() {
    if (!s || s.lat == null || s.lng == null || s.radius_m == null) return;
    const lat = s.lat, lng = s.lng, radiusM = s.radius_m;
    const ok = await consequence({
      title: 'ยืนยันใช้พิกัดนี้เป็นที่ตั้งออฟฟิศ',
      intro: `หลังยืนยัน ระบบจะเปลี่ยนเป็นโหมดบังคับ พนักงานที่เช็คอินนอกรัศมี ${radiusM} เมตรจะต้องรอ HR ตรวจสอบ`,
      lines: [
        { label: 'พิกัด', value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` },
        { label: 'รัศมีที่แนะนำ', value: `${radiusM} เมตร`, emphasis: true },
      ],
      effects: [`คำนวณจากพนักงาน ${s.sample_employees} คน · เช็คอิน ${s.sample_events} ครั้ง · ${s.sample_days} วัน`],
      confirmText: 'ยืนยันใช้พิกัดนี้',
      danger: true,
    });
    if (!ok) return;
    try {
      await apply.mutateAsync({ lat, lng, radius_m: radiusM });
      onApplied(lat, lng, radiusM);
      toast.success('ใช้พิกัดนี้แล้ว — เปลี่ยนเป็นโหมดบังคับ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold mb-1">พิกัดที่ระบบเรียนรู้ได้จากการเช็คอินจริง</div>
      {!confident || !s ? (
        <>
          <p className="text-sm text-gray-600 mb-2">{s?.reason ?? 'ยังไม่มีข้อมูลเช็คอินพอให้เรียนรู้'}</p>
          <div className="text-xs text-gray-500 space-y-0.5">
            <div>เช็คอินสะสม: {s?.sample_events ?? 0} ครั้ง</div>
            <div>จำนวนพนักงาน: {s?.sample_employees ?? 0} คน</div>
            <div>จำนวนวัน: {s?.sample_days ?? 0} วัน</div>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-2">
            คำนวณจากพนักงาน {s.sample_employees} คน · เช็คอิน {s.sample_events} ครั้ง · {s.sample_days} วัน
            {s.p90_dist_m != null && <> · พนักงาน 90% เช็คอินห่างจุดนี้ไม่เกิน {s.p90_dist_m} เมตร</>}
          </p>
          <div className="rounded-lg bg-gray-50 border border-gray-200 divide-y divide-gray-200 mb-3">
            <div className="flex items-baseline justify-between px-3 py-2">
              <span className="text-sm text-gray-600">พิกัด</span>
              <span className="text-sm font-semibold tabular-nums">{s.lat!.toFixed(6)}, {s.lng!.toFixed(6)}</span>
            </div>
            <div className="flex items-baseline justify-between px-3 py-2">
              <span className="text-sm text-gray-600">รัศมีที่แนะนำ</span>
              <span className="text-sm font-semibold">{s.radius_m} เมตร</span>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary text-sm flex-1 justify-center"
            >
              <MapPin className="w-4 h-4" /> ดูจุดนี้ใน Google Maps
            </a>
            <button
              type="button"
              onClick={handleApply}
              disabled={apply.isPending}
              className="btn-primary text-sm flex-1 justify-center"
            >
              {apply.isPending ? 'กำลังบันทึก…' : 'ยืนยันใช้พิกัดนี้'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Web login enable/manage section. Owner sets an email + password here
 * (while logged in via LIFF) so they can later log in from a desktop
 * browser at /login without LINE.
 */
function WebLoginZone() {
  const toast = useToast();
  const [linked, setLinked] = useState<{ email: string | null; last_login_at?: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch<{ email: string | null; last_login_at?: string | null }>('/me/web-identity');
        if (!cancelled) setLinked(r);
      } catch {
        // 401/etc → leave linked null, user will see "ยังไม่ได้ตั้ง"
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    if (!email.trim() || password.length < 8) {
      toast.error('กรอกอีเมลและรหัสผ่านอย่างน้อย 8 ตัว');
      return;
    }
    if (password !== confirm) {
      toast.error('รหัสผ่านยืนยันไม่ตรงกัน');
      return;
    }
    setBusy(true);
    try {
      const r = await apiFetch<{ email: string; linked: boolean }>('/me/web-set-password', {
        method: 'POST',
        body: { email: email.trim(), password },
      });
      setLinked({ email: r.email });
      setEmail('');
      setPassword('');
      setConfirm('');
      toast.success('ตั้งรหัสผ่านเรียบร้อย — ใช้เข้าระบบที่ /login ได้');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตั้งรหัสผ่านไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold mb-2">🔐 เข้าระบบผ่านเว็บ (ไม่ต้องใช้ LINE)</div>
      <p className="text-xs text-gray-500 mb-3">
        ตั้ง email + รหัสผ่าน แล้วเข้าใช้งานจาก desktop ที่ <code className="font-mono">/login</code> ได้ —
        บัญชีเดียวใช้ได้ทั้ง PayPilot / JobPilot / EvalPilot ที่ผูกบริษัทไว้
      </p>

      {loading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : (
        <>
          {linked?.email ? (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2 mb-3">
              ✓ เปิดใช้งานแล้ว: <span className="font-mono">{linked.email}</span>
              {linked.last_login_at && (
                <div className="text-gray-500 mt-0.5">
                  เข้าใช้ล่าสุด: {new Date(linked.last_login_at).toLocaleString('th-TH')}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 mb-3">
              ยังไม่ได้ตั้ง — กรอกแบบฟอร์มด้านล่างเพื่อเปิดใช้งาน
            </div>
          )}

          <div className="space-y-2">
            <div>
              <label className="label">อีเมล</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={linked?.email ?? 'name@example.com'}
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label">{linked?.email ? 'รหัสผ่านใหม่' : 'รหัสผ่าน'}</label>
              <input
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="อย่างน้อย 8 ตัวอักษร"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div>
              <label className="label">ยืนยันรหัสผ่าน</label>
              <input
                type="password"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !email.trim() || password.length < 8 || password !== confirm}
              className="btn-primary w-full text-sm"
            >
              {busy ? 'กำลังบันทึก…' : linked?.email ? 'อัปเดตรหัสผ่าน' : 'เปิดใช้งานเข้าระบบผ่านเว็บ'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function EmployeePortableZone() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ inserted: number; updated: number; total: number; errors: Array<{ row: number; emp_code: string; msg: string }> } | null>(null);

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const jwt = useAuthStore.getState().jwt;
      const res = await fetch(`${API_BASE}/owner/employees/import`, {
        method: 'POST',
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
        body: form,
      });
      const json = (await res.json()) as { data?: typeof result; error?: string; message?: string };
      if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
      setResult(json.data ?? null);
      if (json.data) {
        toast.success(`นำเข้าแล้ว — เพิ่ม ${json.data.inserted} · อัปเดต ${json.data.updated} · ผิดพลาด ${json.data.errors.length}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'นำเข้าไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold mb-1">ย้ายข้อมูลพนักงาน (Owner เท่านั้น)</div>
      <p className="text-xs text-gray-500 mb-3">
        สำหรับย้าย/สำรองรายชื่อพนักงาน (ไฟล์พกพา) — ไม่มีเงินเดือน/เลขบัญชี.
        แก้ใน Excel แล้ว upload กลับมาก็ได้ (emp_code ซ้ำ = อัปเดต)
      </p>
      <div className="grid grid-cols-1 gap-2">
        <button
          onClick={() => downloadCsv('/owner/employees/portable-export', 'employees-portable.csv', toast.error)}
          className="btn-secondary text-sm"
        >
          <Download className="w-4 h-4" /> ดาวน์โหลด CSV
        </button>
        <label className={`btn-secondary text-sm cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-4 h-4" /> {busy ? 'กำลังนำเข้า…' : 'อัปโหลด CSV เพื่อนำเข้า'}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleImport} disabled={busy} />
        </label>
      </div>
      {result && (
        <div className="mt-3 text-xs">
          <div className="text-gray-700">
            ทั้งหมด {result.total} · เพิ่ม <span className="font-semibold text-green-700">{result.inserted}</span> ·
            อัปเดต <span className="font-semibold text-blue-700">{result.updated}</span> ·
            ผิดพลาด <span className="font-semibold text-rose-700">{result.errors.length}</span>
          </div>
          {result.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-rose-700">ดูแถวที่ผิดพลาด ({result.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto pl-4 list-disc text-gray-600">
                {result.errors.map((er, i) => (
                  <li key={i}>แถว {er.row} {er.emp_code ? `(${er.emp_code})` : ''}: {er.msg}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function DeleteTenantZone() {
  const profile = useAuthStore((s) => s.profile);
  const clear = useAuthStore((s) => s.clear);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    setBusy(true);
    try {
      await apiFetch('/owner/delete-tenant', {
        method: 'POST',
        body: { confirmation_text: 'DELETE', tenant_code: code },
      });
      toast.success('ลบบริษัทเรียบร้อย — ออกจากระบบ');
      clear();
      window.location.href = '/';
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-4 border-red-200">
      <div className="font-semibold text-red-700 mb-2 flex items-center gap-2"><Trash2 className="w-5 h-5" /> ลบบริษัท (พื้นที่อันตราย)</div>
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-secondary text-sm text-red-700 border-red-200">
          <Trash2 className="w-4 h-4" /> ลบบริษัทและข้อมูลทั้งหมด
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-red-700">
            ⚠️ การลบจะทำลายกุญแจเข้ารหัสทันที — ข้อมูลเงินเดือนกู้คืนไม่ได้ และบริษัทจะถูก archive
          </p>
          <p className="text-xs text-gray-600">
            พิมพ์รหัสบริษัท <strong className="font-mono">{profile?.tenant_code}</strong> เพื่อยืนยัน
          </p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="tenant_code"
            className="input font-mono"
          />
          <div className="flex gap-2">
            <button onClick={() => { setOpen(false); setCode(''); }} className="btn-secondary text-sm flex-1">ยกเลิก</button>
            <button
              onClick={handleDelete}
              disabled={busy || code.toUpperCase() !== (profile?.tenant_code ?? '').toUpperCase()}
              className="btn-danger text-sm flex-1"
            >
              {busy ? 'กำลังลบ…' : 'ยืนยันลบ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
